import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// Simple toast system for transient success/error/info messages.
// Usage: const { notify } = useToast(); notify('Added to cart');

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
    const [messages, setMessages] = useState([]);
    // message shape: { id, text, type }

    const remove = useCallback((id) => {
        setMessages(msgs => msgs.filter(m => m.id !== id));
    }, []);

    const notify = useCallback((text, { type = 'success', timeout = 2500 } = {}) => {
        if (!text) return;
        const id = Date.now() + Math.random();
        const msg = { id, text, type };
        setMessages(msgs => [...msgs, msg]);
        if (timeout > 0) {
            setTimeout(() => remove(id), timeout);
        }
    }, [remove]);

    // Show a toast whenever a React Query mutation throws (dispatched by queryClient.js)
    useEffect(() => {
        const handler = (e) => notify(e.detail?.message, { type: 'error' });
        window.addEventListener('query:mutation-error', handler);
        return () => window.removeEventListener('query:mutation-error', handler);
    }, [notify]);

    return (
        <ToastContext.Provider value={{ notify }}>
            {children}
            <div
                aria-live="polite"
                aria-atomic="true"
                className="toast-container position-fixed p-3 d-flex flex-column align-items-center"
                style={{
                    zIndex: 1080,
                    width: 'min(400px, 100%)',
                    left: '50%',
                    top: '30%',
                    transform: 'translate(-40%, -40%)',
                    pointerEvents: 'none'
                }}
            >
                {messages.map(m => (
                    <div
                        key={m.id}
                        className={`toast show mb-2 border-0 shadow-sm text-bg-${m.type === 'error' ? 'danger' : m.type === 'info' ? 'info' : 'success'}`}
                        role="status"
                        style={{ pointerEvents: 'auto', width: '100%' }}
                    >
                        <div className="d-flex">
                            <div className="toast-body small fw-medium flex-grow-1">
                                {m.text}
                            </div>
                            <button
                                type="button"
                                className="btn-close btn-close-white me-2 m-auto"
                                aria-label="Close"
                                onClick={() => remove(m.id)}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within ToastProvider');
    return ctx;
};
